import { useEffect, useRef } from "react";

/**
 * A quiet, drifting starfield rendered on a canvas. Purely decorative; sits
 * behind everything. Respects `prefers-reduced-motion` by freezing the drift.
 *
 * Performance: a few hundred stars on a single canvas, redrawn with
 * requestAnimationFrame. Cheap on mobile.
 */
interface Star {
  x: number;
  y: number;
  r: number;
  baseAlpha: number;
  twinklePhase: number;
  twinkleSpeed: number;
  drift: number; // vertical drift speed
}

export default function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let stars: Star[] = [];
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    function seed() {
      // Density scales with screen area, capped for performance.
      const count = Math.min(420, Math.floor((width * height) / 6000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.3 + 0.2,
        baseAlpha: Math.random() * 0.5 + 0.2,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.6 + 0.2,
        drift: Math.random() * 0.04 + 0.01,
      }));
    }

    function resize() {
      width = canvas!.clientWidth;
      height = canvas!.clientHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = width * dpr;
      canvas!.height = height * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    let raf = 0;
    let t = 0;
    function frame() {
      t += 0.016;
      ctx!.clearRect(0, 0, width, height);
      for (const s of stars) {
        const twinkle = reduceMotion
          ? s.baseAlpha
          : s.baseAlpha +
            Math.sin(t * s.twinkleSpeed + s.twinklePhase) * 0.25;
        ctx!.globalAlpha = Math.max(0, Math.min(1, twinkle));
        ctx!.beginPath();
        ctx!.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx!.fillStyle = "#cfe8ff";
        ctx!.fill();

        if (!reduceMotion) {
          s.y += s.drift;
          if (s.y > height) {
            s.y = 0;
            s.x = Math.random() * width;
          }
        }
      }
      ctx!.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    }

    resize();
    frame();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 h-full w-full"
    />
  );
}
